import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../components/DataTable';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import useSharePointList from '../hooks/useSharePointList';
import { createPlant, listPlants } from '../services/plants';
import { listTeam } from '../services/team';
import PlantForm from './PlantForm';
import usePermissions from '../auth/usePermissions';
import usePlantScope from '../auth/usePlantScope';
import { displayFullName } from '../utils/teamDisplay';
import { formatCountry, formatDateShort } from '../utils/formatters';

// Which statuses count as "currently working" for the purposes of showing a
// team member as a plant's RM/LM. Mirrors the filter used on PlantDetail.
const ACTIVE_STATUSES = new Set(['Active', 'On Leave']);

// Plant lifecycle order, "most actively running" first. A plant's
// effective status is the most-active status across all its vehicles —
// so a plant with one Production vehicle and one Wind-Down vehicle
// reads as Production. Plants with no vehicles fall into the catch-all
// at the end. The labels here are user-facing — they appear as the
// section banner text on the index page.
const STATUS_GROUP_ORDER = [
  'Production',
  'Launch',
  'Pre-Launch',
  'Wind-Down',
  'Closed',
  'No Vehicles',
];
// Lower-priority bucket → smaller index. We pick the LARGEST priority
// across a plant's vehicles so the most-active stage wins.
const STATUS_PRIORITY = {
  'Pre-Launch': 1,
  Launch: 2,
  Production: 3,
  'Wind-Down': 0,
  Closed: -1,
};

function effectivePlantStatus(plant) {
  const vehicles = Array.isArray(plant.vehicles) ? plant.vehicles : [];
  let bestKey = null;
  let bestPriority = -Infinity;
  for (const v of vehicles) {
    const status = (v?.status || '').trim();
    if (!status) continue;
    const priority = STATUS_PRIORITY[status];
    if (priority == null) continue;
    if (priority > bestPriority) {
      bestPriority = priority;
      bestKey = status;
    }
  }
  return bestKey || 'No Vehicles';
}

/**
 * Plants index. Managers can create new plants; all roles can click through
 * to each plant's detail page.
 */
export default function Plants() {
  const { canAddPlants } = usePermissions();
  // Regional / Liaison Managers only see plants they're assigned to; Directors
  // and Executives see all. `scoped`/`allows` come from the user's Team record.
  const { scoped, codes: allowedCodes, loading: scopeLoading } = usePlantScope();
  const navigate = useNavigate();
  const { data, loading, error, refresh } = useSharePointList(listPlants);

  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Pull the team roster so we can compute each plant's RM / LM from team
  // members' plantCodes instead of the legacy RegionalManager/LiaisonManager
  // columns on the plant record (which are no longer written). A failed
  // fetch just collapses to "no derived managers" rather than blowing up
  // the table — the core plant rows still render.
  const [team, setTeam] = useState([]);
  useEffect(() => {
    let cancelled = false;
    listTeam()
      .then((rows) => {
        if (!cancelled) setTeam(rows);
      })
      .catch(() => {
        if (!cancelled) setTeam([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Build a { PLANTCODE -> { rms: [...], lms: [...] } } lookup, filtered to
  // currently-working members. Keeps the column render function cheap.
  const managersByPlant = useMemo(() => {
    const map = {};
    (team || []).forEach((m) => {
      if (!ACTIVE_STATUSES.has(m.EmploymentStatus)) return;
      const codes = Array.isArray(m.plantCodes) ? m.plantCodes : [];
      codes.forEach((raw) => {
        const code = (raw || '').toUpperCase();
        if (!code) return;
        if (!map[code]) map[code] = { rms: [], lms: [] };
        if (m.PortalRole === 'RegionalManager') map[code].rms.push(m);
        else if (m.PortalRole === 'LiaisonManager') map[code].lms.push(m);
      });
    });
    return map;
  }, [team]);

  // Enrich each plant row with the derived RM / LM names so sorting and
  // search keep working off plain string columns. Plant-scoped users
  // (Regional / Liaison Managers) only keep the plants assigned to them.
  const rows = useMemo(() => {
    const visible =
      scoped && allowedCodes
        ? (data || []).filter((p) =>
            allowedCodes.has((p.PlantCode || '').toUpperCase()),
          )
        : data || [];
    return visible.map((p) => {
      const bucket = managersByPlant[(p.PlantCode || '').toUpperCase()] || {
        rms: [],
        lms: [],
      };
      return {
        ...p,
        RegionalManager: bucket.rms.map(displayFullName).filter(Boolean).join(', '),
        LiaisonManager: bucket.lms.map(displayFullName).filter(Boolean).join(', '),
      };
    });
  }, [data, managersByPlant, scoped, allowedCodes]);

  const onNew = () => setCreating(true);

  const onSave = useCallback(
    async (model) => {
      setSaving(true);
      try {
        await createPlant(model);
        setCreating(false);
        await refresh();
      } finally {
        setSaving(false);
      }
    },
    [refresh],
  );

  const columns = useMemo(
    () => [
      { key: 'Title', label: 'Plant name' },
      {
        key: 'PlantCode',
        label: 'Code',
        // Match the purple-pill treatment used for plant codes on
        // Suppliers, Team, Dashboard, etc. — keeps the plant identity
        // color consistent everywhere it appears.
        sortValue: (r) => r.PlantCode || '',
        render: (r) =>
          r.PlantCode ? (
            <span className="badge plant">{r.PlantCode}</span>
          ) : (
            <span className="muted">—</span>
          ),
      },
      { key: 'OEM', label: 'OEM', mobileHidden: true },
      {
        key: 'vehicles',
        label: 'Vehicles',
        filter: false,
        mobileHidden: true,
        // Pinned a bit wider than the auto-fit default so multi-vehicle
        // plants (Ram 1500, Ram ProMaster, Wagoneer, etc.) have room
        // to lay their pills out without immediately wrapping.
        width: 220,
        // Sort by vehicle count — more informative than sorting by the array
        // literal. Ties fall back to whatever natural order we already have.
        sortValue: (r) => (r.vehicles || []).length,
        render: (r) => {
          const vs = r.vehicles || [];
          if (vs.length === 0) return <span className="muted">—</span>;
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {vs.map((v, i) => {
                // Pill shows just the vehicle model — OEM/make is
                // already its own column, so duplicating it in the
                // pill (e.g. "Stellantis Ram 1500") was redundant.
                // Legacy single-string `name` records pre-date the
                // make/model split, so fall back to that when the
                // newer `model` field isn't filled in. Program lives
                // in its own column too.
                const label = v.model || v.name || v.make || '';
                // Vehicle pills are always green per the global pill-color
                // convention. The status stays accessible on hover via the
                // title attribute so the info isn't lost.
                return (
                  <span
                    key={i}
                    className="badge vehicle"
                    title={v.status || ''}
                  >
                    {label || '—'}
                  </span>
                );
              })}
            </div>
          );
        },
      },
      { key: 'City', label: 'City', width: 150, mobileHidden: true },
      // State / Country hold short values, so they use tight padding, no
      // sort arrows, and small widths to keep the table from running wide.
      { key: 'State', label: 'State', width: 64, compact: true, sortable: false, mobileHidden: true },
      {
        key: 'Country',
        label: 'Country',
        width: 80,
        compact: true,
        sortable: false,
        mobileHidden: true,
        // Display "USA" for United States (data is left as stored); keeps
        // the column compact and consistent.
        render: (r) => formatCountry(r.Country),
      },
      { key: 'RegionalManager', label: 'Regional mgr', mobileHidden: true },
      { key: 'LiaisonManager', label: 'Liaison mgr', mobileHidden: true },
      // Last Updated — SharePoint's Modified system column. Sort key is
      // the parsed timestamp so newest/oldest ordering is chronological,
      // not lexicographic on the formatted label. Falls back to Created
      // for rows that haven't been edited since they were added.
      {
        key: 'Modified',
        label: 'Last Updated',
        mobileHidden: true,
        sortValue: (r) => new Date(r.Modified || r.Created || 0).getTime(),
        render: (r) => {
          const v = r.Modified || r.Created;
          return v ? formatDateShort(v) : <span className="muted">—</span>;
        },
      },
    ],
    [],
  );

  const onRowClick = (row) => {
    if (row.PlantCode) navigate(`/plants/${encodeURIComponent(row.PlantCode)}`);
  };

  return (
    <>
      <div className="page-header">
        <h1>Plants</h1>
        <div className="actions">
          {canAddPlants && (
            <button className="btn btn-primary" onClick={onNew}>
              + New Plant
            </button>
          )}
        </div>
      </div>

      {(loading || scopeLoading) && <Loading />}
      {error && !loading && (
        <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
          <div className="card-title">Couldn't Load Plants</div>
          <p>{error.message}</p>
          <button className="btn btn-outline btn-sm" onClick={refresh}>
            Retry
          </button>
        </div>
      )}
      {!loading && !scopeLoading && !error && (
        <DataTable
          rows={rows}
          columns={columns}
          onRowClick={onRowClick}
          mobileSearchOnly
          searchLabel="Search Plants"
          emptyMessage="No plants yet. Click “+ New Plant” to add your first one."
          groupBy={{
            getKey: effectivePlantStatus,
            order: STATUS_GROUP_ORDER,
          }}
        />
      )}

      <Modal
        open={creating}
        onClose={() => (saving ? null : setCreating(false))}
        title="New Plant"
      >
        {creating && (
          <PlantForm
            initial={{}}
            saving={saving}
            onSave={onSave}
            onCancel={() => setCreating(false)}
          />
        )}
      </Modal>
    </>
  );
}
